import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@components/common/dialog"
import { Button } from "@components/common/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
import { Checkbox } from "@components/common/checkbox"
import { Label } from "@components/common/label"
import useTranslator from "@hooks/use-translator"
import { toast } from "@hooks/use-toast"
import axiosServices from "@pma/utils/axios"
import { PROJECT_REMINDER_SET_ENDPOINT } from "@pma/utils/api-routes"

interface Props {
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ReminderSettingsModal({ projectId, open, onOpenChange }: Props) {
  const { translate } = useTranslator()
  const [period, setPeriod] = useState<"daily" | "weekly" | "monthly">("weekly")
  const [dayOfWeek, setDayOfWeek] = useState(0)
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const [sendTime, setSendTime] = useState("09:00")
  const [useEmail, setUseEmail] = useState(true)
  const [useWhatsapp, setUseWhatsapp] = useState(true)
  const [loading, setLoading] = useState(false)

  // Convert Israel local time to UTC minutes (handles DST automatically)
  const computeSendAtMinUtc = (time: string) => {
    const [h, m] = time.split(":").map(Number)
    const now = new Date()
    const israelDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0)
    // Use Intl to get the real Israel offset (UTC+2 in winter, UTC+3 in summer)
    const israelOffsetMs = israelDate.getTime() - new Date(israelDate.toLocaleString("en-US", { timeZone: "Asia/Jerusalem" })).getTime()
    const utcDate = new Date(israelDate.getTime() + israelOffsetMs)
    const utcMinutes = utcDate.getUTCHours() * 60 + utcDate.getUTCMinutes()
    return utcMinutes
  }

  // Generate time options (06:00-20:00, 30min steps)
  const timeOptions: { value: string; label: string }[] = []
  for (let h = 6; h <= 20; h++) {
    for (const m of [0, 30]) {
      if (h === 20 && m === 30) break
      const val = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
      timeOptions.push({ value: val, label: val })
    }
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      await axiosServices.post(PROJECT_REMINDER_SET_ENDPOINT, {
        project_id: projectId,
        is_enabled: true,
        period,
        send_at_min_utc: computeSendAtMinUtc(sendTime),
        ...(period === "weekly" && { day_of_week: dayOfWeek }),
        ...(period === "monthly" && { day_of_month: dayOfMonth }),
        use_email: useEmail,
        use_whatsapp: useWhatsapp,
      })
      toast({
        title: translate("Success"),
        description: translate("Reminder settings saved"),
        variant: "default",
      })
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: translate("Error"),
        description: err?.response?.data?.message || translate("Failed to save reminder settings"),
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{translate("Reminder Settings")}</DialogTitle>
          <DialogDescription>
            {"תזכורת תישלח לכל חברי הפרויקט לעדכן את סטטוס המשימות שלהם. התזכורת כוללת את רשימת המשימות הפתוחות בפרויקט."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          {/* Frequency */}
          <div className="flex flex-col gap-2">
            <Label>{translate("Frequency")}</Label>
            <Select value={period} onValueChange={(v: any) => setPeriod(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">{translate("Daily")}</SelectItem>
                <SelectItem value="weekly">{translate("Weekly")}</SelectItem>
                <SelectItem value="monthly">{translate("Monthly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Day of week — shown when period=weekly */}
          {period === "weekly" && (
            <div className="flex flex-col gap-2" dir="rtl">
              <Label>{"יום בשבוע"}</Label>
              <Select value={String(dayOfWeek)} onValueChange={(v: string) => setDayOfWeek(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">{"ראשון"}</SelectItem>
                  <SelectItem value="1">{"שני"}</SelectItem>
                  <SelectItem value="2">{"שלישי"}</SelectItem>
                  <SelectItem value="3">{"רביעי"}</SelectItem>
                  <SelectItem value="4">{"חמישי"}</SelectItem>
                  <SelectItem value="5">{"שישי"}</SelectItem>
                  <SelectItem value="6">{"שבת"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Day of month — shown when period=monthly */}
          {period === "monthly" && (
            <div className="flex flex-col gap-2" dir="rtl">
              <Label>{"יום בחודש"}</Label>
              <Select value={String(dayOfMonth)} onValueChange={(v: string) => setDayOfMonth(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {String(d)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Send time — always shown */}
          <div className="flex flex-col gap-2" dir="rtl">
            <Label>{"שעת שליחה (שעון ישראל)"}</Label>
            <Select value={sendTime} onValueChange={setSendTime}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notification channels */}
          <div className="flex flex-col gap-3">
            <Label>{translate("Notification Channels")}</Label>
            <div className="flex items-center gap-2">
              <Checkbox
                id="reminder-email"
                checked={useEmail}
                onCheckedChange={(v) => setUseEmail(!!v)}
              />
              <Label htmlFor="reminder-email" className="text-sm font-normal cursor-pointer">
                {translate("Email")}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="reminder-whatsapp"
                checked={useWhatsapp}
                onCheckedChange={(v) => setUseWhatsapp(!!v)}
              />
              <Label htmlFor="reminder-whatsapp" className="text-sm font-normal cursor-pointer">
                {translate("WhatsApp")}
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translate("Cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={loading || (!useEmail && !useWhatsapp)}>
            {loading ? translate("Saving...") : translate("Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
